import { Pipe, PipeTransform } from '@angular/core';

/**
 * JoinPipe - конвейер для объединения строк и массивов в одну строку.
 *
 * ```
 * Метод `transform` принимает до четырех параметров:
 *  - Основную строку или массив строк/чисел для объединения.
 *  - Массив строк или чисел для объединения с основной строкой/массивом.
 *  - Разделитель для объединения строк (по умолчанию: '.').
 *  - Позиция основной строки/массива в результирующей строке (по умолчанию: 'end').
 * ```
 *
 * @example
 * {{ 'Hello' | join: ['world', 'Angular'] }} // outputs "world.Angular.Hello"
 * {{ 'Hello' | join: ['world', 'Angular'] : '.' : 'start' }} // outputs "Hello.world.Angular"
 * {{ ['a', 'b', 'c'] | join: [] : ', ' }} // outputs "a, b, c"
 * {{ ['а', 'х'] | join: ['у', 'й'] : '-' : 'start' }} // outputs "а-х-у-й"
 *
 * @extends PipeTransform
 */
@Pipe({
  name: 'join',
  standalone: true,
})
export class JoinPipe implements PipeTransform {
  /**
   * Преобразует входные данные в строку, объединяя основную строку или массив с массивом строк/чисел.
   *
   * @param {string | (string | number)[]} value Основная строка или массив строк/чисел для объединения.
   * @param {(string | number)[]} [args=[]] Массив строк или чисел для объединения.
   * @param {string} [separator='.'] Разделитель для объединения строк.
   * @param {'start' | 'end'} [valuePosition='end'] Позиция основной строки/массива в результирующей строке.
   * @returns {string} Объединенная строка.
   */
  transform(
    value: string | (string | number)[],
    args: (string | number)[] = [],
    separator = '.',
    valuePosition: 'start' | 'end' = 'end',
  ): string {
    const valueString = Array.isArray(value) ? value.map(String).join(separator) : String(value);

    const processedArgs = args.map(String);

    if (valuePosition === 'start') {
      return [valueString, ...processedArgs].join(separator);
    } else {
      return [...processedArgs, valueString].join(separator);
    }
  }
}
